import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Tags, 
  Filter, 
  CheckSquare, 
  Package,
  Loader2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_GROUPS = [
  "GROCERIES",
  "CLEANING MATERIALS", 
  "HARDWARE",
  "GARDEN SUPPLIES",
  "IRRIGATION",
  "AUTOSHOP",
  "ANIMAL CARE",
  "CONSUMER PRODUCTS",
  "RATIONS",
  "BUILDING MATERIAL"
];

export const BulkProductGroupModal = ({ open, onClose, categories, onComplete }) => {
  const [activeTab, setActiveTab] = useState("filter");
  const [loading, setLoading] = useState(false);
  const [groupStats, setGroupStats] = useState(null);
  
  // Filter-based assignment
  const [filterType, setFilterType] = useState("category");
  const [filterValue, setFilterValue] = useState("");
  const [targetGroup, setTargetGroup] = useState("");
  
  // Manual selection
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [productsLoading, setProductsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 50;
  
  // Unique values for filters
  const [suppliers, setSuppliers] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (open) {
      fetchGroupStats();
      fetchFilterOptions();
    }
  }, [open]);

  useEffect(() => {
    if (activeTab === "select" && open) {
      fetchProducts();
    }
  }, [activeTab, searchQuery, page, open]);

  const fetchGroupStats = async () => {
    try {
      const res = await axios.get(`${API}/products/groups`);
      setGroupStats(res.data);
    } catch (err) {
      console.error("Error fetching group stats:", err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      // Get unique suppliers
      const suppRes = await axios.get(`${API}/suppliers`);
      setSuppliers(suppRes.data || []);
      
      // Get unique departments
      const deptRes = await axios.get(`${API}/categories/departments`);
      setDepartments(deptRes.data || []);
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      const params = new URLSearchParams({
        skip: ((page - 1) * pageSize).toString(),
        limit: pageSize.toString()
      });
      if (searchQuery) {
        params.append("q", searchQuery);
      }
      
      const res = await axios.get(`${API}/products?${params}`);
      setProducts(res.data || []);
      
      // Get total count
      const countRes = await axios.get(`${API}/products/count${searchQuery ? `?q=${searchQuery}` : ''}`);
      setTotalProducts(countRes.data?.count || res.data?.length || 0);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleFilterAssign = async () => {
    if (!filterValue || !targetGroup) {
      toast.error("Please select a filter value and target group");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/products/bulk-assign-group-by-filter`, {
        filter_type: filterType,
        filter_value: filterValue,
        product_group: targetGroup
      });
      
      toast.success(res.data.message);
      fetchGroupStats();
      setFilterValue("");
      onComplete?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                       "Failed to assign products";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAssign = async () => {
    if (selectedProducts.size === 0 || !targetGroup) {
      toast.error("Please select products and a target group");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/products/bulk-assign-group`, {
        product_ids: Array.from(selectedProducts),
        product_group: targetGroup
      });
      
      toast.success(res.data.message);
      fetchGroupStats();
      setSelectedProducts(new Set());
      fetchProducts();
      onComplete?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                       "Failed to assign products";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const selectAllOnPage = () => {
    const newSet = new Set(selectedProducts);
    products.forEach(p => newSet.add(p.id));
    setSelectedProducts(newSet);
  };

  const deselectAll = () => {
    setSelectedProducts(new Set());
  };

  const getFilterOptions = () => {
    switch (filterType) {
      case "category":
        return categories.map(c => c.name);
      case "supplier":
        return suppliers.map(s => s.name);
      case "department":
        return departments.map(d => d.name);
      default:
        return [];
    }
  };

  const totalPages = Math.ceil(totalProducts / pageSize);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-blue-600" />
            Bulk Assign Product Groups
          </DialogTitle>
        </DialogHeader>

        {/* Group Stats Summary */}
        {groupStats && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Product Group Distribution</span>
              <span className="text-xs text-gray-500">
                {groupStats.unassigned_count} of {groupStats.total_products} unassigned
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {[...PRODUCT_GROUPS].sort().map(group => (
                <Badge 
                  key={group} 
                  variant={groupStats.group_counts[group] ? "default" : "outline"}
                  className="text-[10px]"
                >
                  {group}: {groupStats.group_counts[group] || 0}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="filter" className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Assign by Filter
            </TabsTrigger>
            <TabsTrigger value="select" className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              Select Products
            </TabsTrigger>
          </TabsList>

          {/* Filter-based Assignment Tab */}
          <TabsContent value="filter" className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3">
                Assign all products matching a filter to a group
              </h4>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Filter By</Label>
                  <Select value={filterType} onValueChange={(v) => { setFilterType(v); setFilterValue(""); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="supplier">Supplier</SelectItem>
                      <SelectItem value="department">Department</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Filter Value</Label>
                  <Select value={filterValue} onValueChange={setFilterValue}>
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${filterType}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {[...getFilterOptions()].sort((a, b) => a.localeCompare(b)).map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Assign to Group</Label>
                  <Select value={targetGroup} onValueChange={setTargetGroup}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select group" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...PRODUCT_GROUPS].sort().map(group => (
                        <SelectItem key={group} value={group}>{group}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                className="mt-4 w-full" 
                onClick={handleFilterAssign}
                disabled={loading || !filterValue || !targetGroup}
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Tags className="h-4 w-4 mr-2" />}
                Assign All Matching Products
              </Button>
            </div>
            
            <div className="text-sm text-gray-600">
              <p><strong>Example:</strong> Select "Category" → "Plumbing" → "HARDWARE" to assign all Plumbing products to the Hardware group.</p>
            </div>
          </TabsContent>

          {/* Manual Selection Tab */}
          <TabsContent value="select" className="flex-1 flex flex-col overflow-hidden mt-4">
            <div className="flex items-center gap-4 mb-3">
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="flex-1"
              />
              <Select value={targetGroup} onValueChange={setTargetGroup}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Target group" />
                </SelectTrigger>
                <SelectContent>
                  {[...PRODUCT_GROUPS].sort().map(group => (
                    <SelectItem key={group} value={group}>{group}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={selectAllOnPage}>
                  Select Page
                </Button>
                <Button size="sm" variant="outline" onClick={deselectAll}>
                  Clear Selection
                </Button>
                <span className="text-sm text-gray-600">
                  {selectedProducts.size} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Page {page} of {totalPages || 1}
                </span>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto border rounded-lg">
              {productsLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : products.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-gray-400">
                  No products found
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="w-10 p-2"></th>
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2">Category</th>
                      <th className="text-left p-2">Current Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <tr 
                        key={product.id} 
                        className={`border-b hover:bg-gray-50 cursor-pointer ${selectedProducts.has(product.id) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleProductSelection(product.id)}
                      >
                        <td className="p-2 text-center">
                          <Checkbox 
                            checked={selectedProducts.has(product.id)}
                            onCheckedChange={() => toggleProductSelection(product.id)}
                          />
                        </td>
                        <td className="p-2">
                          <div className="font-medium">{product.name}</div>
                          <div className="text-xs text-gray-500">{product.plu_code}</div>
                        </td>
                        <td className="p-2 text-gray-600">{product.category}</td>
                        <td className="p-2">
                          {product.product_group ? (
                            <Badge variant="secondary">{product.product_group}</Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">Unassigned</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <Button 
              className="mt-3 w-full" 
              onClick={handleSelectAssign}
              disabled={loading || selectedProducts.size === 0 || !targetGroup}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Tags className="h-4 w-4 mr-2" />}
              Assign {selectedProducts.size} Products to {targetGroup || "..."}
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
